import { useRef, useState } from 'react'
import { FileText, Upload, X } from 'lucide-react'
import { download_template_salesminer_admin_organization_uploads_path } from "@/routes"

interface FileUploadProps {
    selectedFile: File | null
    onFileSelect: (file: File | null) => void
}

export default function FileUpload({ selectedFile, onFileSelect }: FileUploadProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragOver, setIsDragOver] = useState(false)

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragOver(false)

        const files = e.dataTransfer.files
        if (files.length > 0) {
            const file = files[0]
            if (isValidFile(file)) {
                onFileSelect(file)
            }
        }
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
            const file = files[0]
            if (isValidFile(file)) {
                onFileSelect(file)
            }
        }
    }

    const isValidFile = (file: File) => {
        const validTypes = ['application/x-yaml', 'text/yaml', 'text/x-yaml', '.yml', '.yaml']
        const isYaml = validTypes.some(type =>
            file.type === type || file.name.toLowerCase().endsWith('.yml') || file.name.toLowerCase().endsWith('.yaml')
        )

        if (!isYaml) {
            alert('Please select a YAML file (.yml or .yaml)')
            return false
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            alert('File size must be less than 10MB')
            return false
        }

        return true
    }

    const removeFile = () => {
        onFileSelect(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    return (
        <div className="bg-white shadow sm:rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Upload YAML File</h3>

            <div
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                    isDragOver
                        ? 'border-indigo-400 bg-indigo-50'
                        : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="space-y-1 text-center">
                    {selectedFile ? (
                        <div className="space-y-3">
                            <FileText className="mx-auto h-12 w-12 text-green-400"/>
                            <div className="flex items-center justify-center space-x-3">
                                <div className="text-center">
                                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={removeFile}
                                    className="inline-flex items-center p-1 border border-transparent rounded-full text-red-600 hover:bg-red-50"
                                >
                                    <X className="h-4 w-4"/>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Upload className="mx-auto h-12 w-12 text-gray-400"/>
                            <div className="flex text-sm text-gray-600">
                                <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                                >
                                    <span>Upload a file</span>
                                    <input
                                        ref={fileInputRef}
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        accept=".yml,.yaml"
                                        onChange={handleFileSelect}
                                        className="sr-only"
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">YAML files only (.yml or .yaml), up to 10MB</p>
                        </>
                    )}
                </div>
            </div>

            <div className="mt-4 flex justify-between">
                <a
                    href={download_template_salesminer_admin_organization_uploads_path()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    download
                >
                    <FileText className="h-4 w-4 mr-2"/>
                    Download Template
                </a>
            </div>
        </div>
    )
}